import { Controller } from "@hotwired/stimulus"

// Phase 2.10 Tier 4 — read-only per-sequence final-state map.
//
// The server pre-resolves `legacy_finance_final_map_json(project,
// doc_type, sequences)` and renders it on the page in a hidden
// element with this controller. JS-rendered finance tables (Pay
// Request, Change Order, etc.) read this map on init to stamp
// `data-final="true"` on rows they emit, plus inline-mount a small
// gold-seal Final indicator next to the sequence number.
//
// Why a sibling Stimulus controller and not a method on the
// finance controller itself: every legacy-finance Stimulus
// controller in the codebase (8 of them) renders rows differently;
// a sibling controller exposes the lookup once, by doc_type, in a
// way every finance controller can read via DOM query without
// having to register dependencies on each other.
export default class extends Controller {
  static values = {
    docType: String,
    finalMap: Object,
  }

  connect() {
    if (this.element.id !== `legacy-final-map-${this.docTypeValue}`) {
      this.element.id = `legacy-final-map-${this.docTypeValue}`
    }
    // Expose for non-Stimulus consumers (older finance controllers).
    window.LegacyFinalMap = window.LegacyFinalMap || {}
    window.LegacyFinalMap[this.docTypeValue] = this.finalMapValue || {}
  }

  isFinal(sequence) {
    const map = this.finalMapValue || {}
    return map[String(sequence)] === true
  }

  // Decorates a freshly-rendered row by sequence number. Finance
  // controllers call this after building each row:
  //
  //   const final = window.LegacyFinalMap?.pay_request?.[seq] === true
  //   if (final) row.dataset.final = "true"
  //
  // Stamps the data attribute + injects a small gold-seal indicator
  // into the row's first cell.
  static decorate(row, docType, sequence) {
    const map = window.LegacyFinalMap?.[docType] || {}
    if (map[String(sequence)] !== true) return
    row.dataset.final = "true"
    // Tiny inline gold-seal (no Heroicons dependency from JS — the
    // SVG is small enough to embed). Matches the
    // `_final_badge.html.erb` server-rendered look.
    const badge = document.createElement("span")
    badge.className =
      "inline-flex items-center gap-1 px-1.5 py-0.5 ml-1 text-[11px] font-semibold uppercase tracking-wide rounded bg-amber-100 text-amber-800"
    badge.title = "Final"
    badge.innerHTML =
      '<svg class="w-3 h-3" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">' +
      '<path fill-rule="evenodd" d="M16.403 12.652a3 3 0 0 0 0-5.304 3 3 0 0 0-3.75-3.751 3 3 0 0 0-5.305 0 3 3 0 0 0-3.751 3.75 3 3 0 0 0 0 5.305 3 3 0 0 0 3.75 3.751 3 3 0 0 0 5.305 0 3 3 0 0 0 3.751-3.75Zm-2.546-4.46a.75.75 0 0 0-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 1 0-1.06 1.061l2.5 2.5a.75.75 0 0 0 1.137-.089l4-5.5Z" clip-rule="evenodd"/>' +
      '</svg>FINAL'
    const firstCell = row.querySelector("td, th, div")
    if (firstCell) firstCell.appendChild(badge)
  }
}
